import * as Sentry from '@sentry/nextjs';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Address } from 'viem';
import { mainnet } from 'viem/chains';
import { ZodError } from 'zod';

import { ApiError } from '@/lib/errors/api';

import { CHAIN_IDS } from '@/constants/chain';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { getTokenPair } from '@/utils/amplitude';
import { zeroExApiFetcher } from '@/utils/fetcher';
import type { IntentsPrice } from '@/utils/intents/types';
import { isEthereumAddress } from '@/utils/models';
import { isTokenAddressNativeAsset } from '@/utils/multichain';
import { routes } from '@/utils/routes';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';
import { getFeeRequestParams } from '@/utils/swap-fee-params';

import { liquiditySourcesEventHandlers } from '../store/liquiditySources';
import { useLiquiditySourcesStore } from '../store/liquiditySources';
import { useTokenResult } from './tokenQueries/useTokenResult';
import { useSwapFeeQuery } from './transactionModule/useSwapFeeQuery';
import { useMatchaWallets } from './useMatchaWallets';
import { useResolvedSlippage } from './useResolvedSlippage';
import { UseSwapPriceParams } from './useSwapPrice';

export type IntentsPriceQueryOptions = Omit<
  UseQueryOptions<IntentsPrice, ApiError | ZodError | Error>,
  'queryKey' | 'queryFn'
>;

/**
 * Display-only Matcha Auto price from the standalone Intents API. When no
 * wallet is connected the request omits the taker, so the taker-relative
 * `issues.balance`/`issues.allowance` come back null — balance gating happens
 * in `useIntents`, not from this response.
 */
export function useIntentsPrice(
  query: UseSwapPriceParams | null,
  options: IntentsPriceQueryOptions,
): UseQueryResult<IntentsPrice, ApiError | ZodError | Error> {
  const {
    sellTokenAddress,
    sellTokenChainId,
    buyTokenAddress,
    buyTokenChainId,
    sellAmount,
    isGasless = false,
    telemetry: { buyAmountDecimal = '', buyAmountUsd = 0 } = {},
  } = query ?? {};

  Sentry.setTag('app.0x_api_version', 2);

  const { token: sellTokenResult } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyTokenResult } = useTokenResult(buyTokenAddress, buyTokenChainId);
  const sellTokenSymbol = sellTokenResult?.symbol;
  const buyTokenSymbol = buyTokenResult?.symbol;

  // get parameters
  const chainId = sellTokenChainId ?? buyTokenChainId ?? mainnet.id;
  const { ethereumWallet } = useMatchaWallets();
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);
  const { swapFeeBps, swapFeeRecipient, swapFeeToken } = useSwapFeeQuery({
    buyTokenAddress:
      buyTokenAddress && isEthereumAddress(buyTokenAddress) ? buyTokenAddress : undefined,
    buyTokenChainId,
    sellTokenAddress:
      sellTokenAddress && isEthereumAddress(sellTokenAddress) ? sellTokenAddress : undefined,
    sellTokenChainId,
  });

  const excludedSources = useLiquiditySourcesStore(
    liquiditySourcesEventHandlers,
  ).getExcludedSources(chainId);

  // Mirror the intents quote so the price estimate uses the same tolerance.
  const {
    bps: resolvedSlippageBps,
    isAuto,
    isReady: isSlippageReady,
  } = useResolvedSlippage({
    chainId,
    sellToken: sellTokenResult,
    buyToken: buyTokenResult,
  });

  // build URL
  const url = useMemo(() => {
    if (
      !query ||
      parseInt(query.sellAmount, 10) === 0 || // don't fetch a zero amount
      query.sellTokenChainId !== query.buyTokenChainId || // tokens can't be on different chains
      !isGasless || // If Matcha Auto is off, we don't want to fetch an intents price
      isTokenAddressNativeAsset(query.sellTokenAddress, query.sellTokenChainId) || // selling a native token is not supported
      query.sellTokenChainId === CHAIN_IDS.SOLANA ||
      query.buyTokenChainId === CHAIN_IDS.SOLANA
    ) {
      return null;
    }

    const params = new URLSearchParams({
      chainId: chainId.toString(),
      buyToken: query.buyTokenAddress,
      sellToken: query.sellTokenAddress,
      sellAmount: query.sellAmount,
      ...getFeeRequestParams({ swapFeeBps, swapFeeRecipient, swapFeeToken }),
    });
    if (ethereumWallet) params.set('taker', ethereumWallet.address);
    if (excludedSources) params.set('excludedSources', excludedSources);
    if (recipientAddress) params.set('recipient', recipientAddress);
    params.set('slippageBps', resolvedSlippageBps.toString());

    return routes.api.INTENTS_PRICE + '?' + params.toString();
  }, [
    query,
    isGasless,
    chainId,
    swapFeeBps,
    swapFeeRecipient,
    swapFeeToken,
    ethereumWallet,
    excludedSources,
    recipientAddress,
    resolvedSlippageBps,
  ]);

  // build context for metrics & error reporting
  const context = useMemo(
    () => ({
      buy_token_address: buyTokenAddress,
      buy_token_amount: buyAmountDecimal,
      buy_token_symbol: buyTokenSymbol,
      chain_id: chainId,
      order_type: 'market_auto',
      pair: getTokenPair(
        sellTokenAddress && sellTokenSymbol
          ? { address: sellTokenAddress as Address, chainId: chainId, symbol: sellTokenSymbol }
          : undefined,
        buyTokenAddress && buyTokenSymbol
          ? { address: buyTokenAddress as Address, chainId: chainId, symbol: buyTokenSymbol }
          : undefined,
      ),
      sell_token_address: sellTokenAddress,
      sell_token_amount: sellAmount,
      sell_token_symbol: sellTokenSymbol,
      usd_dollar_amount: buyAmountUsd,
      wallet_connected_address: ethereumWallet?.address ?? null,
      swap_fee_bps: swapFeeBps,
      swap_fee_recipient: swapFeeRecipient,
      excluded_sources: excludedSources,
    }),
    [
      buyAmountDecimal,
      buyAmountUsd,
      buyTokenAddress,
      buyTokenSymbol,
      chainId,
      excludedSources,
      sellAmount,
      sellTokenAddress,
      sellTokenSymbol,
      swapFeeBps,
      swapFeeRecipient,
      ethereumWallet,
    ],
  );

  const { enabled: callerEnabled = true, ...restOptions } = options;
  return useQuery<IntentsPrice, ApiError | ZodError | Error>({
    queryKey: ['intents-price', url, isAuto],
    queryFn: async () => withSentSlippage(await zeroExApiFetcher(url!, context), url!, isAuto),
    ...restOptions,
    // Computed last so it overrides any `enabled` in `restOptions`; we
    // AND-merge so the leaf still self-disables when the URL guard fails
    // even if the caller passes `enabled: true`. `isSlippageReady` holds the
    // price until Auto resolves, so it is fetched once at the final tolerance.
    enabled: url !== null && callerEnabled && isSlippageReady,
  });
}
